import { styled } from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { paneIsCompact } from "work/_views/work-pane-layout";

/**
 * Every page these are drawn on — the three proposal pages and the work agreement — declares the
 * `work-pane` container, so each rule below asks the pane's width rather than the window's. The two
 * are not the same question: the navigation drawer and the page's own right-hand drawer take width a
 * media query cannot see, which is what #4964 was about. See `work-pane-layout`.
 */

export const HeadControls = styled("div")(
  ({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    paddingLeft: theme.spacing(2),
  }));

/**
 * No horizontal padding of its own: this sits inside the page's sticky header, and that band's
 * inset is `workPaneHeaderGutters`. A `paddingLeft` here as well indented the tab strip inside the
 * title above it, and the rule that removed it below `md` is the one that left the strip flush
 * against the navigation drawer once "compact" stopped meaning "narrow window".
 */
export const Header = styled("section")(
  ({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingBottom: theme.spacing(1),
    position: "sticky",
    top: "0px",
    zIndex: 10,
  }));

export const TabsContainer = styled(Tabs)(
  ({ theme }) => ({
    flexDirection: "row",
    "&.MuiTabs-root": {
      overflow: "hidden",
      "& .MuiTabs-scroller": {
        [paneIsCompact(theme)]: {
          paddingTop: 0,
        },
        paddingTop: theme.spacing(0.5),
      },
    },
    "& .MuiTabs-scrollButtons.Mui-disabled": {
      opacity: "0.3",
    },
  }));

export const Content = styled("section")(
  ({ theme }) => ({
    alignItems: 'center',
    display: "flex",
    flex: 1,
    flexDirection: "row",
    height: '100%',
    minHeight: '25rem',
    minWidth: 0,
    overflow: "hidden",
    width: '100%',
    [paneIsCompact(theme)]: {
      alignItems: "stretch",
      flex: "1 0 auto",
      height: "auto",
      minHeight: 0,
      overflow: "visible",
    },
  }));

export const TabContent = styled("section", {
  shouldForwardProp: (prop) => prop !== "disableBottomPadding",
})<{ disableBottomPadding?: boolean }>(
  ({ disableBottomPadding, theme }) => ({
    alignItems: "stretch",
    display: "flex",
    flex: 1,
    flexDirection: "column",
    height: '100%',
    justifyContent: "center",
    // Four pixels of room for what a field paints outside itself.
    //
    // The rings a proposal field wears — the issue traverser's pulse, a focus ring — are box
    // shadows spread up to 3px beyond the control, and the left-hand column of every tab begins
    // flush against this element's content edge, so `overflow: hidden` cut the left of the ring
    // off while leaving the other three sides intact. The clip itself has to stay: it is what
    // makes a wide fee schedule scroll inside the pane instead of stretching it (53c3ffbb3). So
    // the boundary moves rather than the clipping — an inline padding with the matching negative
    // margin puts the clip edge outside the ring while leaving every child where it was.
    marginInline: theme.spacing(-0.5),
    minHeight: 0,
    minWidth: 0,
    overflow: "hidden",
    paddingInline: theme.spacing(0.5),
    width: "100%",
    [paneIsCompact(theme)]: {
      flex: "1 0 auto",
      height: "auto",
      justifyContent: "flex-start",
      overflow: "visible",
      paddingBottom: disableBottomPadding ? 0 : theme.spacing(2),
      paddingTop: theme.spacing(1),
    },
  }));

export const ProposalTab = styled(Tab)(
  () => ({
    "&.MuiTab-root": {
      flexShrink: 0,
      justifyContent: "flex-end",
      maxWidth: "none",
      minWidth: "max-content",
      overflow: "visible",
      whiteSpace: "nowrap",
    },
  }));

export const ButtonContainer = styled("div")(
  () => ({
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
  }));
